import React, { useState, useRef, useEffect } from 'react'
import { applyFormatting, createRichText, richTextToHTML } from '../utils/richTextUtils'

/**
 * Inline Text Editor Component - Canva-like text editing experience
 * Appears directly over text elements when double-clicked
 */
const InlineTextEditor = ({
  element,
  onUpdate,
  onClose,
  onTextSelect,
  isVisible,
  caretPoint
}) => {
  // Initialize rich text structure
  const initRichText = element?.richText || createRichText(element?.content || '')
  const [richText, setRichText] = useState(initRichText)
  const [content, setContent] = useState(initRichText.content || '')
  const [selectedText, setSelectedText] = useState('')
  const [selectionRange, setSelectionRange] = useState(null)
  const editorRef = useRef(null)
  const lastRangeRef = useRef(null)

  // Build HTML from richText model
  const spansToHTML = (content, spans = []) => {
    if (!content) return ''
    if (!spans.length) return content.replace(/\n/g, '<br/>')
    const sorted = [...spans].sort((a, b) => a.start - b.start)
    let html = ''
    let cursor = 0
    sorted.forEach(({ start, end, styles }) => {
      if (start > cursor) {
        html += content.slice(cursor, start).replace(/\n/g, '<br/>')
      }
      const text = content.slice(start, end).replace(/\n/g, '<br/>')
      const styleStr = Object.entries(styles || {})
        .map(([k, v]) => `${k.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${v}`)
        .join(';')
      html += `<span data-rich="1" style="${styleStr}">${text}</span>`
      cursor = end
    })
    if (cursor < content.length) {
      html += content.slice(cursor).replace(/\n/g, '<br/>')
    }
    return html
  }

  // Convert editor DOM back to richText model
  const parseDOMToRichText = () => {
    const root = editorRef.current
    if (!root) return { content: '', spans: [] }

    // Collect text nodes in order
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
    let node
    let textContent = ''
    const spans = []
    let offset = 0

    while ((node = walker.nextNode())) {
      const part = node.nodeValue || ''
      if (!part) continue

      // Compute styles by walking up ancestors
      let el = node.parentElement
      const style = {}
      while (el && el !== root) {
        const s = el.getAttribute && el.getAttribute('style')
        if (s) {
          s.split(';').forEach(rule => {
            const [prop, val] = rule.split(':').map(x => x && x.trim())
            if (prop && val) {
              // Convert kebab-case to camelCase
              const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
              style[camel] = val
            }
          })
        }
        el = el.parentElement
      }
      if (Object.keys(style).length) {
        spans.push({ start: offset, end: offset + part.length, styles: style })
      }
      textContent += part
      offset += part.length
    }
    return { content: textContent, spans }
  }

  // Update content when element changes
  useEffect(() => {
    setContent(element?.richText?.content || element?.content || '')
    // Render initial HTML
    const html = spansToHTML(element?.richText?.content || element?.content || '', element?.richText?.spans || [])
    if (editorRef.current) editorRef.current.innerHTML = html
  }, [element?.content, element?.richText])

  useEffect(() => {
    if (!isVisible || !editorRef.current) return

    // helper to place caret safely inside editor
    const placeCaret = () => {
      const root = editorRef.current
      const sel = window.getSelection()
      if (!sel) return
      sel.removeAllRanges()

      const addWithin = (range) => {
        if (!range) return false
        const container = range.commonAncestorContainer || range.startContainer
        if (container && root.contains(container)) {
          sel.addRange(range)
          return true
        }
        return false
      }

      // Try by point first
      if (caretPoint) {
        if (document.caretRangeFromPoint) {
          const r = document.caretRangeFromPoint(caretPoint.x, caretPoint.y)
          if (addWithin(r)) return
        }
        if (document.caretPositionFromPoint) {
          const pos = document.caretPositionFromPoint(caretPoint.x, caretPoint.y)
          if (pos) {
            const r = document.createRange()
            r.setStart(pos.offsetNode, pos.offset)
            r.collapse(true)
            if (addWithin(r)) return
          }
        }
      }

      // Fallback: place caret at end of the last text node; create one if needed
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
      let lastText = null, n
      while ((n = walker.nextNode())) lastText = n
      const r = document.createRange()
      if (lastText) {
        r.setStart(lastText, lastText.nodeValue?.length || 0)
      } else {
        const textNode = document.createTextNode('')
        root.appendChild(textNode)
        r.setStart(textNode, 0)
      }
      r.collapse(true)
      sel.addRange(r)
    }

    // Focus and place caret on next frame to ensure DOM is painted
    const raf1 = requestAnimationFrame(() => {
      editorRef.current.focus({ preventScroll: true })
      const raf2 = requestAnimationFrame(() => placeCaret())
      // store for cleanup
      editorRef.__raf2 = raf2
    })
    editorRef.__raf1 = raf1

    // expose formatter hook for properties bar
    window.currentTextEditor = {
      hasSelection: false,
      selectedText: '',
      selectionRange: null,
      applyFormatting: applyFormattingToSelection
    }

    return () => {
      if (editorRef.__raf1) cancelAnimationFrame(editorRef.__raf1)
      if (editorRef.__raf2) cancelAnimationFrame(editorRef.__raf2)
    }
  }, [isVisible, caretPoint])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editorRef.current && !editorRef.current.contains(event.target)) {
        handleSave()
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible, content])

  const handleSave = () => {
    const rich = parseDOMToRichText()
    onUpdate(element.id, { richText: rich, content: rich.content })
    onClose()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  const handleTextSelection = () => {
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const selectedText = range.toString()
      setSelectedText(selectedText)
      setSelectionRange({
        start: range.startOffset,
        end: range.endOffset
      })
      // cache the live Range so we can restore it after focus changes
      lastRangeRef.current = range.cloneRange()

      if (onTextSelect) {
        onTextSelect(selectedText, range, {
          start: range.startOffset,
          end: range.endOffset
        })
      }
    }
  }

  // This function will be called from the properties bar
  const applyFormattingToSelection = (format, value) => {
    const selection = window.getSelection()
    const root = editorRef.current
    if (!root) return

    // If focus moved to properties bar, restore the last cached selection range
    if (!selection || selection.rangeCount === 0 || !root.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      if (lastRangeRef.current) {
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(lastRangeRef.current)
      } else {
        // No selection -> apply to element default
        onUpdate(element.id, { [format]: value })
        return
      }
    }

    const range = window.getSelection().getRangeAt(0)
    if (!root.contains(range.commonAncestorContainer)) {
      onUpdate(element.id, { [format]: value })
      return
    }

    // Apply formatting to current selection
    const currentRichText = parseDOMToRichText()
    const selectionStart = range.startOffset
    const selectionEnd = range.endOffset

    // Create formatting object
    const formatting = {}
    if (format === 'bold') formatting.fontWeight = value ? 'bold' : 'normal'
    else if (format === 'italic') formatting.fontStyle = value ? 'italic' : 'normal'
    else if (format === 'underline') formatting.textDecoration = value ? 'underline' : 'none'
    else if (format === 'color') formatting.color = value
    else if (format === 'fontSize') formatting.fontSize = value + 'px'
    else if (format === 'fontFamily') formatting.fontFamily = value

    // Apply formatting using rich text utils
    const updatedRichText = applyFormatting(currentRichText, selectionStart, selectionEnd, formatting)
    setRichText(updatedRichText)

    // Update the editor display
    const html = richTextToHTML(updatedRichText)
    if (editorRef.current) {
      editorRef.current.innerHTML = html
    }

    // Update the element
    onUpdate(element.id, {
      richText: updatedRichText,
      content: updatedRichText.content
    })

    // Wrap selection in a span with style
    const span = document.createElement('span')
    span.setAttribute('data-rich', '1')
    switch (format) {
      case 'bold':
      case 'fontWeight':
        span.style.fontWeight = value === true ? 'bold' : value || 'bold'
        break
      case 'italic':
      case 'fontStyle':
        span.style.fontStyle = value === true ? 'italic' : value || 'italic'
        break
      case 'underline':
      case 'textDecoration':
        span.style.textDecoration = value || 'underline'
        break
      case 'color':
        span.style.color = value
        break
      case 'fontSize':
        span.style.fontSize = typeof value === 'number' ? `${value}px` : value
        break
      case 'fontFamily':
        span.style.fontFamily = value
        break
      case 'letterSpacing':
        span.style.letterSpacing = typeof value === 'number' ? `${value}px` : value
        break
      case 'wordSpacing':
        span.style.wordSpacing = typeof value === 'number' ? `${value}px` : value
        break
      case 'lineHeight':
        span.style.lineHeight = value
        break
      case 'backgroundColor':
        span.style.backgroundColor = value
        break
      case 'WebkitTextStroke':
        span.style.WebkitTextStroke = value // e.g. "1px #000"
        break
      case 'fontKerning':
        span.style.fontKerning = value
        break
      case 'stroke': {
        const width = (element.strokeWidth ?? 1)
        // Behind fill stroke effect using text-shadow with rounded corners
        if (width > 0) {
          const shadows = [];
          // Use more angles for smoother rounded corners
          for (let angle = 0; angle < 360; angle += 30) {
            const radian = (angle * Math.PI) / 180;
            const x = Math.cos(radian) * width;
            const y = Math.sin(radian) * width;
            shadows.push(`${x}px ${y}px ${width * 0.3}px ${value}`);
          }
          span.style.textShadow = shadows.join(', ');
        } else {
          span.style.textShadow = 'none';
        }
        break
      }
      case 'strokeWidth': {
        const color = (element.stroke || element.color || '#000')
        const width = typeof value === 'number' ? value : parseFloat(value) || 1;
        // Behind fill stroke effect using text-shadow with rounded corners
        if (width > 0) {
          const shadows = [];
          // Use more angles for smoother rounded corners
          for (let angle = 0; angle < 360; angle += 30) {
            const radian = (angle * Math.PI) / 180;
            const x = Math.cos(radian) * width;
            const y = Math.sin(radian) * width;
            shadows.push(`${x}px ${y}px ${width * 0.3}px ${color}`);
          }
          span.style.textShadow = shadows.join(', ');
        } else {
          span.style.textShadow = 'none';
        }
        break
      }
      case 'textShadow': {
        if (typeof value === 'object' && value.enabled) {
          const { offsetX, offsetY, blur, color, opacity, gradient } = value;

          if (gradient && gradient.stops?.length > 0) {
            // For gradient text shadows, use the first color with opacity blending
            const gradientColor = gradient.stops[0].color || '#000000';
            const shadowOpacity = opacity || 0.5;
            const rgbaColor = gradientColor.startsWith('#')
              ? `rgba(${parseInt(gradientColor.slice(1, 3), 16)}, ${parseInt(gradientColor.slice(3, 5), 16)}, ${parseInt(gradientColor.slice(5, 7), 16)}, ${shadowOpacity})`
              : gradientColor;
            span.style.textShadow = `${offsetX || 2}px ${offsetY || 2}px ${blur || 4}px ${rgbaColor}`;
          } else {
            // Solid color shadow
            const shadowOpacity = opacity || 0.5;
            const shadowColor = color || '#000000';
            const rgbaColor = shadowColor.startsWith('#')
              ? `rgba(${parseInt(shadowColor.slice(1, 3), 16)}, ${parseInt(shadowColor.slice(3, 5), 16)}, ${parseInt(shadowColor.slice(5, 7), 16)}, ${shadowOpacity})`
              : shadowColor;
            span.style.textShadow = `${offsetX || 2}px ${offsetY || 2}px ${blur || 4}px ${rgbaColor}`;
          }
        } else if (typeof value === 'string') {
          span.style.textShadow = value
        } else {
          span.style.textShadow = 'none'
        }
        break
      }
      case 'textGlow': {
        if (typeof value === 'object' && value.enabled) {
          const { blur = 10, color = '#FFFFFF', intensity = 0.8, spread = 5 } = value;
          
          // Create glow effect using multiple text-shadows in all directions
          // Glow is different from shadow - it's even around the text with no offset
          const glowColor = color.startsWith('#')
            ? `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, ${intensity})`
            : color;
          
          const shadows = [];
          // Create multiple layers of glow for smooth effect
          const layers = Math.max(3, Math.floor(spread / 2));
          for (let i = 1; i <= layers; i++) {
            const layerBlur = (blur * i) / layers;
            const layerOpacity = intensity * (1 - (i / layers) * 0.5);
            const layerColor = color.startsWith('#')
              ? `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, ${layerOpacity})`
              : color;
            
            // Create glow in all directions (circular)
            for (let angle = 0; angle < 360; angle += 45) {
              const radian = (angle * Math.PI) / 180;
              const offsetX = Math.cos(radian) * (spread * i / layers);
              const offsetY = Math.sin(radian) * (spread * i / layers);
              shadows.push(`${offsetX}px ${offsetY}px ${layerBlur}px ${layerColor}`);
            }
          }
          
          span.style.textShadow = shadows.join(', ');
        } else {
          span.style.textShadow = span.style.textShadow || 'none'
        }
        break
      }
      case 'shadowColor':
      case 'shadowBlur':
      case 'shadowOffsetX':
      case 'shadowOffsetY': {
        // Legacy shadow handling for backward compatibility
        const color = format === 'shadowColor' ? value : (element.shadowColor || '#000')
        const blur = format === 'shadowBlur' ? (parseFloat(value) || 0) : (element.shadowBlur || 0)
        const offX = format === 'shadowOffsetX' ? (parseFloat(value) || 0) : (element.shadowOffsetX || 0)
        const offY = format === 'shadowOffsetY' ? (parseFloat(value) || 0) : (element.shadowOffsetY || 0)
        span.style.textShadow = `${offX}px ${offY}px ${blur}px ${color}`
        break
      }
      case 'textAlign':
        // alignment is for container; fall back to element update
        onUpdate(element.id, { textAlign: value })
        return
      default:
        // Fallback: allow setting any inline CSS property
        try {
          if (typeof span.style[format] !== 'undefined') {
            span.style[format] = value
          }
        } catch {}
        break
    }

    try {
      range.surroundContents(span)
    } catch (err) {
      // Non-contiguous ranges etc.; fallback: insert span and move contents
      const contents = range.extractContents()
      span.appendChild(contents)
      range.insertNode(span)
    }

    // Update model from DOM
    const rich = parseDOMToRichText()
    onUpdate(element.id, { richText: rich })

    // Update cached range to encompass the new span
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      lastRangeRef.current = sel.getRangeAt(0).cloneRange()
    }
  }

  // Expose helper onto global for properties bar to call while it has focus
  useEffect(() => {
    if (!isVisible) return
    window.currentTextEditor = window.currentTextEditor || {}
    window.currentTextEditor.applyFormatting = applyFormattingToSelection
    window.currentTextEditor.restoreSelection = () => {
      const sel = window.getSelection()
      if (lastRangeRef.current && sel) {
        sel.removeAllRanges()
        sel.addRange(lastRangeRef.current)
      }
    }
  }, [isVisible, selectedText, selectionRange])

  if (!isVisible) return null

  return (
    <>
      {/* Inline Editor - fills the entire element space */}
      <div
        className="inline-text-editor w-full h-full bg-white border-2 border-blue-400 rounded-lg shadow-lg z-[2000]"
        onMouseDownCapture={(e) => { e.stopPropagation() }}
        onMouseUpCapture={(e) => { e.stopPropagation() }}
        onClickCapture={(e) => { e.stopPropagation() }}
        onDoubleClickCapture={(e) => { e.stopPropagation() }}
        style={{ pointerEvents: 'auto' }}
      >
        <div
          ref={editorRef}
          contentEditable
          className={`w-full h-full p-1 outline-none resize-none cursor-text select-text flex ${element.verticalAlign === 'top' ? 'items-start' :
              element.verticalAlign === 'bottom' ? 'items-end' : 'items-center'
            } ${element.textAlign === 'left' ? 'justify-start' :
              element.textAlign === 'right' ? 'justify-end' :
                element.textAlign === 'justify' ? 'justify-between' : 'justify-center'
            }`}
          style={{
            fontSize: element.fontSize || 16,
            fontFamily: element.fontFamily || 'Arial',
            color: element.color || element.fill || '#000000',
            caretColor: element.color || element.fill || '#000000',
            fontWeight: element.fontWeight || 'normal',
            fontStyle: element.fontStyle || 'normal',
            lineHeight: element.lineHeight || 1.2,
            letterSpacing: `${element.letterSpacing || 0}px`,
            wordSpacing: `${element.wordSpacing || 0}px`,
            fontKerning: element.fontKerning || 'auto',
            textAlign: element.textAlign || 'center',
            textDecoration: element.textDecoration || 'none',
            direction: element.direction || 'ltr',
            backgroundColor: 'transparent',
            minHeight: '100%',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
          onInput={(e) => {
            // no-op here; we update on save/close to preserve spans
          }}
          onKeyDown={handleKeyDown}
          onMouseDownCapture={(e) => { e.stopPropagation() }}
          onMouseUpCapture={(e) => { e.stopPropagation() }}
          onMouseUp={handleTextSelection}
          onKeyUp={handleTextSelection}
          suppressContentEditableWarning={true}
          dangerouslySetInnerHTML={{ __html: spansToHTML(element?.richText?.content || element?.content || '', element?.richText?.spans || []) }}
        />
      </div>
    </>
  )
}

export default InlineTextEditor
